import { useEffect, useState } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { listBgtUsers, type BgtUserDto } from '@/lib/api/bgt-users';
import { updateResident } from '@/lib/api/residents';
import { listVoiceUsers, type VoiceUserDto } from '@/lib/api/voice-users';

const PAGE_SIZE = 20;

/** 교체 대상 계정 종류 */
export type AccountKind = 'bgt' | 'voice';

/** BGT/보이스 계정을 하나의 Autocomplete로 다루기 위한 정규화 형태 */
interface Option {
  key: string;
  bgtId: string | null;
  voiceId: number | null;
  primary: string;
  secondary: string | null;
  alreadyMapped: boolean;
}

export interface SwapTarget {
  id: string;
  roomNumber: string | null;
  name: string | null;
  userId: string | null;
  voiceUserId: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  kind: AccountKind;
  resident: SwapTarget | null;
}

const LABEL: Record<AccountKind, string> = {
  bgt: '브레인가드테라피',
  voice: '보이스웰니스 계정',
};

// 서버가 실제로 검색하는 필드와 문구를 맞춘다.
//   bgt   — user.code(환자번호) / user.name
//   voice — users.loginId(아이디) / users.name
const SEARCH_PLACEHOLDER: Record<AccountKind, string> = {
  bgt: '환자번호 또는 이름으로 검색',
  voice: '아이디 또는 이름으로 검색',
};
const SEARCH_HINT: Record<AccountKind, string> = {
  bgt: '환자번호·이름 검색',
  voice: '아이디·이름 검색',
};

function nextPageParam(last: { page: number; limit: number; total: number }): number | undefined {
  return last.page * last.limit >= last.total ? undefined : last.page + 1;
}

function toBgtOption(u: BgtUserDto): Option {
  return {
    key: `bgt:${u.id}`,
    bgtId: u.id,
    voiceId: null,
    primary: u.name ?? u.patientCode ?? u.id,
    secondary: [u.patientCode, u.birthDate].filter(Boolean).join(' · ') || null,
    alreadyMapped: u.alreadyMapped,
  };
}

function toVoiceOption(u: VoiceUserDto): Option {
  return {
    key: `voice:${u.id}`,
    bgtId: null,
    voiceId: u.id,
    primary: `${u.loginId} · ${u.name}`,
    secondary: u.alreadyMapped
      ? `이미 연결됨${u.mappedRoomNumber ? ` (${u.mappedRoomNumber}호)` : ''}`
      : null,
    alreadyMapped: u.alreadyMapped,
  };
}

/** 연결된 계정이 첫 페이지에 없을 수 있어 id로 역추적 */
async function findById(kind: AccountKind, id: string | number): Promise<Option | null> {
  let page = 1;
  for (;;) {
    if (kind === 'bgt') {
      const res = await listBgtUsers({ page, limit: PAGE_SIZE, unmappedOnly: false });
      const found = res.items.find((u) => u.id === id);
      if (found) return toBgtOption(found);
      if (page * res.limit >= res.total) return null;
    } else {
      const res = await listVoiceUsers({ page, limit: PAGE_SIZE, unmappedOnly: false });
      const found = res.items.find((u) => u.id === id);
      if (found) return toVoiceOption(found);
      if (page * res.limit >= res.total) return null;
    }
    page += 1;
  }
}

/**
 * 입소자의 브레인가드테라피 / 보이스웰니스 계정만 교체하는 경량 다이얼로그.
 *
 * 등록/수정 폼(ResidentFormDialog)은 호실·구분까지 함께 다뤄서 계정만 바꾸려 할 때
 * 매번 신규 등록처럼 느껴진다. 입소자 상세 패널의 '연결 계정' 영역에서 이 창을 연다.
 *
 * 계정 교체는 그 입소자의 점수·이력이 통째로 다른 사람 것으로 바뀌는 조작이라
 * 저장 직전에 확인 단계를 한 번 둔다.
 */
export function AccountSwapDialog({ open, onClose, kind, resident }: Props) {
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Option | null>(null);
  const [current, setCurrent] = useState<Option | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentId = kind === 'bgt' ? (resident?.userId ?? null) : (resident?.voiceUserId ?? null);

  // 타이핑마다 조회하지 않도록 디바운스. 서버 검색이라 키 입력과 분리한다.
  useEffect(() => {
    const t = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const optionsQuery = useInfiniteQuery({
    queryKey: ['account-swap', kind, search],
    queryFn: async ({ pageParam }) => {
      if (kind === 'bgt') {
        const res = await listBgtUsers({
          page: pageParam,
          limit: PAGE_SIZE,
          search: search || undefined,
        });
        return { ...res, options: res.items.map(toBgtOption) };
      }
      const res = await listVoiceUsers({
        page: pageParam,
        limit: PAGE_SIZE,
        search: search || undefined,
      });
      return { ...res, options: res.items.map(toVoiceOption) };
    },
    initialPageParam: 1,
    getNextPageParam: nextPageParam,
    enabled: open,
  });

  // 열 때마다 현재 연결 계정을 복원한다.
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setCurrent(null);
      setInput('');
      setSearch('');
      setError(null);
      setConfirming(false);
      return;
    }
    if (currentId == null) {
      setSelected(null);
      setCurrent(null);
      return;
    }
    let cancelled = false;
    setRestoring(true);
    void findById(kind, currentId)
      .then((found) => {
        if (cancelled) return;
        setSelected(found);
        setCurrent(found);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind, currentId]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!resident) throw new Error('대상 입소자가 없습니다');
      return updateResident(
        resident.id,
        kind === 'bgt'
          ? { userId: selected?.bgtId ?? null }
          : { voiceUserId: selected?.voiceId ?? null },
      );
    },
    onSuccess: () => {
      // 목록·요약·상세가 구 계정 캐시를 쓰지 않도록 일괄 무효화
      for (const key of [
        'residents',
        'residents-usage-summary',
        'bgt-users',
        'voice-users',
        'account-swap',
        'voice-today',
        'voice-history',
        'timeline',
      ]) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      onClose();
    },
    onError: (e: unknown) => {
      setConfirming(false);
      setError(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '계정 변경에 실패했습니다',
      );
    },
  });

  const options = optionsQuery.data?.pages.flatMap((p) => p.options) ?? [];
  const unchanged = (selected?.key ?? null) === (current?.key ?? null);

  const who = resident
    ? `${resident.roomNumber ? `${resident.roomNumber}호` : '호실 미배정'} ${resident.name ?? ''}`.trim()
    : '';

  return (
    <>
      <Dialog open={open && !confirming} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>
          {who} — {LABEL[kind]}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={options}
              value={selected}
              loading={optionsQuery.isLoading || restoring}
              getOptionLabel={(o) => o.primary}
              isOptionEqualToValue={(o, v) => o.key === v.key}
              // 서버 검색이므로 클라이언트 필터링은 끈다.
              filterOptions={(x) => x}
              // inputValue는 제어하지 않는다 — 제어하면 복원된 계정 라벨이 입력창에
              // 표시되지 않는다. 표시는 MUI에 맡기고 검색어만 관찰한다.
              onInputChange={(_, value, reason) => {
                // 'reset'(선택 후 라벨 복원)은 검색어로 취급하지 않는다.
                if (reason === 'input') setInput(value);
                if (reason === 'clear') setInput('');
              }}
              // 선택된 값이 있어도 입력하면 바로 다시 검색할 수 있게 한다.
              selectOnFocus
              handleHomeEndKeys
              autoHighlight
              onChange={(_, value) => {
                setSelected(value);
                setError(null);
              }}
              slotProps={{
                listbox: {
                  onScroll: (event) => {
                    const el = event.currentTarget;
                    if (
                      el.scrollTop + el.clientHeight >= el.scrollHeight - 8 &&
                      optionsQuery.hasNextPage &&
                      !optionsQuery.isFetchingNextPage
                    ) {
                      void optionsQuery.fetchNextPage();
                    }
                  },
                },
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  <Stack>
                    <Typography variant="body2">{option.primary}</Typography>
                    {option.secondary && (
                      <Typography
                        variant="caption"
                        color={option.alreadyMapped ? 'warning.main' : 'text.secondary'}
                      >
                        {option.secondary}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
              noOptionsText={
                optionsQuery.isFetching || input.trim() !== search ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="body2">불러오는 중…</Typography>
                  </Stack>
                ) : (
                  '결과 없음'
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`연결할 ${LABEL[kind]}`}
                  placeholder={SEARCH_PLACEHOLDER[kind]}
                  helperText={`${SEARCH_HINT[kind]} · 비우면 연결 해제`}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="disabled" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {optionsQuery.isFetching && !optionsQuery.isFetchingNextPage && (
                            <CircularProgress size={16} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />

            {selected?.alreadyMapped && !unchanged && (
              <Alert severity="warning">
                이 {LABEL[kind]}은 이미 다른 입소자에 연결되어 있습니다. 저장이 거절될 수 있습니다.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="contained"
            disabled={unchanged || restoring}
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
          >
            변경
          </Button>
        </DialogActions>
      </Dialog>

      {/* 계정을 바꾸면 이 입소자의 점수·이력이 다른 사람 것으로 바뀐다 — 오조작 방어 */}
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{LABEL[kind]}을 정말 변경할까요?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>{who}</strong>
              </Typography>
              <Typography variant="body2">현재: {current?.primary ?? '미연결'}</Typography>
              <Typography variant="body2">변경: {selected?.primary ?? '연결 해제'}</Typography>
              <Alert severity="warning" sx={{ mt: 1 }}>
                변경하면 이 입소자 화면에 표시되는 점수·이력이 새 계정 기준으로 바뀝니다.
              </Alert>
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={mutation.isPending}>
            취소
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '변경 중…' : '변경'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
